import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Download, Trash2, Shield, AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function WhoopDataManager() {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleDownloadData = async () => {
    setIsDownloading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Fetch all WHOOP data
      const [recovery, sleep, cycle, workouts] = await Promise.all([
        supabase.from('whoop_recovery_data').select('*').eq('user_id', user.id),
        supabase.from('whoop_sleep_data').select('*').eq('user_id', user.id),
        supabase.from('whoop_cycle_data').select('*').eq('user_id', user.id),
        supabase.from('whoop_workout_data').select('*').eq('user_id', user.id),
      ]);

      const exportData = {
        export_date: new Date().toISOString(),
        user_id: user.id,
        data: {
          recovery: recovery.data || [],
          sleep: sleep.data || [],
          cycles: cycle.data || [],
          workouts: workouts.data || [],
        },
        metadata: {
          total_records: (recovery.data?.length || 0) + (sleep.data?.length || 0) + 
                         (cycle.data?.length || 0) + (workouts.data?.length || 0),
          data_types: ['recovery', 'sleep', 'cycles', 'workouts'],
        }
      };

      // Create and download JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `whoop-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('WHOOP data downloaded successfully');
    } catch (error) {
      console.error('Error downloading WHOOP data:', error);
      toast.error('Failed to download WHOOP data');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDeleteData = async () => {
    setIsDeleting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Delete all WHOOP data
      const deletePromises = [
        supabase.from('whoop_recovery_data').delete().eq('user_id', user.id),
        supabase.from('whoop_sleep_data').delete().eq('user_id', user.id),
        supabase.from('whoop_cycle_data').delete().eq('user_id', user.id),
        supabase.from('whoop_workout_data').delete().eq('user_id', user.id),
        supabase.from('whoop_connections').delete().eq('user_id', user.id),
      ];

      await Promise.all(deletePromises);

      // Also clear whoop fields from profile
      await supabase
        .from('profiles')
        .update({ 
          whoop_user_id: null, 
          whoop_connected_at: null 
        })
        .eq('user_id', user.id);

      toast.success('All WHOOP data deleted successfully');
      setShowDeleteDialog(false);
      
      // Reload page to reflect changes
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error('Error deleting WHOOP data:', error);
      toast.error('Failed to delete WHOOP data');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Your WHOOP Data Rights
          </CardTitle>
          <CardDescription>
            Manage and control your WHOOP data stored in Therappi
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <Shield className="h-4 w-4" />
            <AlertDescription>
              You have full control over your WHOOP data. You can download a copy anytime or 
              permanently delete all data we've stored.
            </AlertDescription>
          </Alert>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium text-sm">Download Your Data</p>
                <p className="text-xs text-muted-foreground">
                  Export all your WHOOP data as JSON
                </p>
              </div>
              <Button
                onClick={handleDownloadData}
                disabled={isDownloading}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                {isDownloading ? 'Downloading...' : 'Download'}
              </Button>
            </div>

            <div className="flex items-center justify-between p-3 border border-accent/50 rounded-lg">
              <div>
                <p className="font-medium text-sm text-accent">Delete All Data</p>
                <p className="text-xs text-muted-foreground">
                  Permanently remove all WHOOP data from Therappi
                </p>
              </div>
              <Button
                onClick={() => setShowDeleteDialog(true)}
                disabled={isDeleting}
                variant="outline"
                size="sm"
                className="gap-2 border-accent text-accent hover:bg-accent/10"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </div>
          </div>

          <div className="pt-4 border-t text-xs text-muted-foreground space-y-2">
            <p><strong>Privacy Compliance:</strong> We comply with GDPR, CCPA, and other data privacy laws.</p>
            <p><strong>No Third-Party Sharing:</strong> Your WHOOP data is never sold or shared with third parties.</p>
            <p><strong>Secure Storage:</strong> All data is encrypted at rest and in transit.</p>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Permanently Delete WHOOP Data?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This will permanently delete all WHOOP data we've stored, including:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>All recovery scores and HRV data</li>
                <li>All sleep tracking data</li>
                <li>All strain and workout data</li>
                <li>Your WHOOP connection settings</li>
              </ul>
              <p className="font-semibold pt-2">
                This action cannot be undone. You can reconnect your WHOOP account later, 
                but historical data will need to sync again.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteData}
              className="bg-accent text-accent-foreground hover:bg-accent/90"
            >
              {isDeleting ? 'Deleting...' : 'Yes, Delete Everything'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
